import React, { useState, useEffect } from 'react';
import { MatchResult } from '../../services/signalRService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMars, faVenus, faCheck, faTimes, faVideo, faCircleUser, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import styles from './styles.module.css';

interface MatchesDialogProps {
  matches: MatchResult[];
  currentUserId: string;
  onAcceptMatch: (matchId: string) => void;
  onDeclineMatch: (matchId: string) => void;
  onClose: () => void;
}

interface UserInfo {
  id: string;
  age?: number;
  gender?: string;
  username?: string;
  shortBio?: string;
  whoAmI?: string;
  interests?: string;
}

export const MatchesDialog: React.FC<MatchesDialogProps> = ({ 
  matches, 
  currentUserId, 
  onAcceptMatch, 
  onDeclineMatch,
  onClose 
}) => {
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  const toggleUserDetails = (userId: string) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const renderUserInfo = (user: UserInfo) => {
    const userGender = user.gender || 'other';
    const isUserMale = userGender.toLowerCase() === 'male';
    const hasAdditionalInfo = user.shortBio || user.whoAmI || user.interests;

    return (
      <div className={styles.userInfo}>
        <div className={styles.basicInfo}>
          <FontAwesomeIcon 
            icon={userGender == "other" ? faCircleUser : (isUserMale ? faMars : faVenus)} 
            className={styles.genderIcon}
            style={{ color: userGender == "other" ? '#000' : (isUserMale ? '#007bff' : '#ff3366') }}
          />
          <span className={styles.age}>{user.age}</span>
          {user.username && <span className={styles.username}>{user.username}</span>}
          {hasAdditionalInfo && (
            <button 
              className={styles.infoButton}
              onClick={() => toggleUserDetails(user.id)}
              title="View more details"
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </button>
          )}
        </div>
        
        {expandedUser === user.id && (
          <div className={styles.expandedInfo}>
            {user.shortBio && (
              <div className={styles.infoSection}>
                <h4>Bio</h4>
                <p>{user.shortBio}</p>
              </div>
            )}
            {user.whoAmI && (
              <div className={styles.infoSection}>
                <h4>About Me</h4>
                <p>{user.whoAmI}</p>
              </div>
            )}
            {user.interests && (
              <div className={styles.infoSection}>
                <h4>Interests</h4>
                <p>{user.interests}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <h2>Your Matches!</h2>
        <div className={styles.matchList}>
          {matches.map((match) => {
            const isCurrentUserFirst = match.user1.id === currentUserId;
            const matchedUser = isCurrentUserFirst ? match.user2 : match.user1;
            const matchId = match.id;
            const isFullyAccepted = match.status === 'Accepted';
            const hasCurrentUserAccepted = isCurrentUserFirst ? match.acceptedByUser1 : match.acceptedByUser2;
            const hasOtherUserAccepted = isCurrentUserFirst ? match.acceptedByUser2 : match.acceptedByUser1;

            return (
              <div key={matchId} className={styles.matchItem}>
                <div className={styles.matchContent}>
                  {renderUserInfo(matchedUser)}
                  {!isFullyAccepted && match.status !== 'Rejected' && !hasCurrentUserAccepted && (
                    <div className={styles.actions}>
                      <button 
                        className={`${styles.actionButton} ${styles.accept}`}
                        onClick={() => onAcceptMatch(matchId)}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                      <button 
                        className={`${styles.actionButton} ${styles.decline}`}
                        onClick={() => onDeclineMatch(matchId)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}
                  {hasCurrentUserAccepted && !hasOtherUserAccepted && (
                    <div className={styles.waitingMessage}>
                      Waiting for other person...
                    </div>
                  )}
                </div>
                <div className={styles.acceptanceStatus}>
                  <div className={`${styles.statusDot} ${hasCurrentUserAccepted ? styles.accepted : ''}`}>
                    You
                  </div>
                  <div className={`${styles.statusDot} ${hasOtherUserAccepted ? styles.accepted : ''}`}>
                    Match
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <button className={styles.closeButton} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}; 